import React, { useEffect } from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space, Typography, Upload, Image } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getPostInfo, updatePost } from '../../services/post';
import { getAllCategories } from '../../services/category';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';
import BundledEditor from '../../components/BundledEditor';

const itemName = 'Bài viết';
const { Text, Title } = Typography;

const EditPost = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { data: itemInfo } = useQuery({ queryKey: ['postInfo', id], queryFn: () => getPostInfo(id) });

  // categories for select
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryOptions = [
    { value: 0, label: 'Chọn danh mục' },
    ...(((allCategoriesRes?.data || []).filter((c) => Number(c?.type) === 1).map((c) => ({ value: c.id, label: c.title }))) || []),
  ];

  useEffect(() => {
    if (itemInfo) {
      form.setFieldsValue({
        title: itemInfo?.title,
        category_id: itemInfo?.category_id ?? 0,
        thumb: itemInfo?.thumb,
        description: itemInfo?.description,
        content: typeof itemInfo?.content === 'string' ? itemInfo.content : '',
        order: itemInfo?.order ?? 0,
        status: itemInfo?.status,
      });
    }
  }, [form, itemInfo]);

  const { mutate: mutateUpdate, isPending } = useMutation({
    mutationFn: ({ id, payload }) => updatePost(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Cập nhật ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    // guard: must choose a real category (not the default 0)
    if (Number(data?.category_id || 0) === 0) {
      notification.warning({ message: 'Vui lòng chọn danh mục cho bài viết!' });
      return;
    }
    const payload = {
      title: data?.title,
      category_id: Number(data?.category_id || 0),
      thumb: data?.thumb,
      description: data?.description,
      content: data?.content,
      order: Number(data?.order || 0),
      status: data?.status,
    };
    mutateUpdate({ id, payload });
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) return false;
    const url = await uploadImageCDN(file, () => {}, 'toancohien_post');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  return (
    <Form name="edit-post" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item name="title" label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}>
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="category_id"
            label={<Text>Danh mục <span className="text-red-700">*</span></Text>}
            initialValue={0}
            rules={[
              {
                validator: (_, value) => (Number(value || 0) > 0 ? Promise.resolve() : Promise.reject(new Error('Vui lòng chọn danh mục'))),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={categoryOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="thumb" label={<Text>Ảnh đại diện</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
            <Input
              placeholder={`Nhập URL hoặc tải lên file...`}
              addonBefore={
                <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadThumb} className="hover:cursor-pointer">
                  <UploadOutlined /> Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="status" label={<Text>Trạng thái <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}>
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="description" label={<Text>Mô tả ngắn</Text>}>
            <Input placeholder={`Nhập mô tả ngắn...`} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="content" label={<Text>Nội dung (HTML)</Text>} valuePropName="value" trigger="onEditorChange" getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}>
            <BundledEditor
              init={{
                license_key: 'gpl',
                height: 500,
                menubar: false,
                automatic_uploads: true,
                file_picker_types: 'image',
                plugins: ['advlist','autolink','lists','link','image','charmap','preview','anchor','searchreplace','visualblocks','code','fullscreen','insertdatetime','media','table','help','wordcount'],
                toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image | removeformat | code | help',
                extended_valid_elements: 'iframe[src|frameborder|style|scrolling|class|width|height|name|align|title|sandbox],a[name|href|target|rel],script[src|type|defer|async],div[*]',
                file_picker_callback: (cb, value, meta) => {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', 'image/*');
                  input.onchange = async () => {
                    const file = input.files[0];
                    if (!checkValidUpload(file)) return;
                    const url = await uploadImageCDN(file, () => {}, 'toancohien_post');
                    if (url) {
                      const safeUrl = encodeURI(url);
                      cb(safeUrl, { title: file.name });
                    }
                  };
                  input.click();
                },
                images_upload_handler: async (blobInfo, progress) => {
                  const blob = blobInfo.blob();
                  if (!checkValidUpload(blob)) return '';
                  const url = await uploadImageCDN(blob, () => {}, 'toancohien_post');
                  return encodeURI(url || '');
                },
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={0}>
            <InputNumber min={0} className="w-full" placeholder="Nhập thứ tự" size="large" />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>Hủy bỏ</Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>Lưu lại</Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditPost;
